import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import generateOtaLink from 'frontend-utils/utils/url/generate-ota-link';
import GeneralTablesCellsTdLink from 'lumen/components/general/tables/cells/td-link';
import GeneralTablesCellsTdWithCustomClass from 'lumen/components/general/tables/cells/td-with-custom-class';
import ParityTablesCellsTdPlatform from 'lumen/components/parity/tables/cells/td-platform';
import ParityTablesCellsTdPosCountry from 'lumen/components/parity/tables/cells/td-pos-country';
import ParityTablesCellsTdProof from 'lumen/components/parity/tables/cells/td-proof';
import type { Parity } from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

import styles from './table-issues.module.css';

const COLUMNS = {
  ARRIVAL_DATE: 'arrival_date',
  CHANNEL: 'channel',
  SOURCE: 'source',
  PLATFORM: 'platform',
  POS: 'pos',
  BRAND_RATE: 'brand_rate',
  CHANNEL_RATE: 'channel_rate',
  DIFFERENCE: 'difference',
  PROOF: 'proof',
};

interface ParityTablesTableIssuesSignature {
  Args: {
    isLoading?: boolean;
    issues: Parity[];
  };
}

export default class ParityTablesTableIssues extends Component<ParityTablesTableIssuesSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service('parity/logger') declare parityLogger: Services['parity/logger'];

  initialSorts = [
    {
      valuePath: `${COLUMNS.ARRIVAL_DATE}.value`,
      isAscending: true,
    },
  ];

  get columns(): CustomEmberTableColumn[] {
    const dateColumn = {
      name: this.intl.t('general.arrival_date'),
      valuePath: `${COLUMNS.ARRIVAL_DATE}.value`,
      displayValuePath: `${COLUMNS.ARRIVAL_DATE}.formattedValue`,
      textAlign: 'left' as const,
      hasRightBorder: true,
      minWidth: 120,
      maxWidth: 120,
    };
    const channelColumn = {
      name: this.intl.t('parity.channel'),
      valuePath: COLUMNS.CHANNEL,
      textAlign: 'left' as const,
    };
    const sourceColumn = {
      name: this.intl.t('general.source'),
      valuePath: COLUMNS.SOURCE,
      textAlign: 'left' as const,
    };
    const platformColumn = {
      name: this.intl.t('general.platform.plural', { count: 1 }),
      valuePath: COLUMNS.PLATFORM,
      tdComponent: ParityTablesCellsTdPlatform,
      textAlign: 'center' as const,
    };
    const posColumn = {
      name: this.intl.t('general.pos_short'),
      valuePath: COLUMNS.POS,
      tdComponent: ParityTablesCellsTdPosCountry,
      textAlign: 'center' as const,
      hasRightBorder: true,
    };
    const brandRateColumn = {
      name: this.intl.t('parity.base_rate'),
      valuePath: `${COLUMNS.BRAND_RATE}.value`,
      displayValuePath: COLUMNS.BRAND_RATE,
      tdComponent: GeneralTablesCellsTdLink,
      textAlign: 'right' as const,
      tdClassNames: 'tabular-nums',
    };
    const channelRateColumn = {
      name: this.intl.t('parity.channel_rate'),
      valuePath: `${COLUMNS.CHANNEL_RATE}.value`,
      displayValuePath: COLUMNS.CHANNEL_RATE,
      tdComponent: GeneralTablesCellsTdLink,
      textAlign: 'right' as const,
      tdClassNames: 'tabular-nums',
      hasRightBorder: true,
    };
    const differenceColumn = {
      name: this.intl.t('general.difference'),
      valuePath: `${COLUMNS.DIFFERENCE}.value`,
      displayValuePath: COLUMNS.DIFFERENCE,
      tdComponent: GeneralTablesCellsTdWithCustomClass,
      textAlign: 'right' as const,
      tdClassNames: 'tabular-nums',
    };
    const proofColumn = {
      name: this.intl.t('parity.proof'),
      valuePath: `${COLUMNS.PROOF}.sortValue`,
      displayValuePath: COLUMNS.PROOF,
      tdComponent: ParityTablesCellsTdProof,
      textAlign: 'left' as const,
    };

    return [
      dateColumn,
      channelColumn,
      sourceColumn,
      platformColumn,
      posColumn,
      brandRateColumn,
      channelRateColumn,
      differenceColumn,
      proofColumn,
    ];
  }

  get isMarketDemandVisible(): boolean {
    return this.access.hasParityEnhancedDemandMetric;
  }

  get rows(): CustomEmberTableRow[] {
    const { id: hotelId, isTripadvisorRebranding } =
      this.hotelAndMarketService.hotel;
    return this.args.issues.map((issue) => {
      const brandRedirectUrl = generateOtaLink(
        OTA_IDS.BRANDDOTCOM,
        issue.hotelinfo.id!,
        hotelId!,
        isTripadvisorRebranding,
        issue.period,
        issue.maxPersons,
        'app_parity',
        false,
        issue.pos,
      );

      const channelRedirectUrl = generateOtaLink(
        issue.ota.id!,
        issue.hotelinfo.id!,
        hotelId!,
        isTripadvisorRebranding,
        issue.period,
        issue.maxPersons,
        'app_parity',
        false,
        issue.pos,
      );

      if (this.isMarketDemandVisible) {
        // TODO: PI-2182: Market Demand column
      }

      return {
        [COLUMNS.ARRIVAL_DATE]: {
          value: issue.unixArrivalDate,
          formattedValue: issue.formattedArrivalDate,
        },
        [COLUMNS.CHANNEL]: issue.formattedChannel,
        [COLUMNS.SOURCE]: issue.formattedSource,
        [COLUMNS.PLATFORM]: issue.platform,
        [COLUMNS.POS]: issue.pos,
        [COLUMNS.BRAND_RATE]: {
          value: issue.brandRate,
          formattedValue: issue.formattedBrandRate,
          redirectUrl: brandRedirectUrl,
        },
        [COLUMNS.CHANNEL_RATE]: {
          value: issue.value,
          formattedValue: issue.formattedChannelRate,
          redirectUrl: channelRedirectUrl,
        },
        [COLUMNS.DIFFERENCE]: {
          value: issue.differenceWithBaserate,
          formattedValue: issue.formattedDifferenceWithBaserate,
          customClass: issue.differenceWithBaserateClass,
        },
        [COLUMNS.PROOF]: {
          sortValue: issue.sortValue,
          formattedValue: issue.formattedScreenshotParity,
          redirectUrl: issue.screenshotUrl,
          isScreenshotSupported: issue.isScreenshotSupported,
          onDoubleClick: () => this.parityLogger.logParityScreenshot(issue),
        },
      };
    });
  }

  <template>
    <div class={{styles.container}}>
      <PrismTable
        @columns={{this.columns}}
        @enableAutoSortsUpdate={{true}}
        @initialSorts={{this.initialSorts}}
        @isLoading={{@isLoading}}
        @renderAll={{false}}
        @rows={{this.rows}}
      />
    </div>
  </template>
}
